import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BUSINESS } from "../data";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Phone, Globe, MapPin, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Landscape Design",
  "Lawn & Garden Maintenance",
  "Irrigation, Patios & Stone Walls",
  "Leaf & Seasonal Cleanup",
  "Other / Not Sure",
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service) {
      toast.error("Please fill in your name, phone and service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      toast.success("Quote request sent! We'll be in touch soon.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#E3DFD5]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14">
        <div>
          <p className="overline">Get a Free Quote</p>
          <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight leading-tight mt-4 text-[#1A2118]">
            Let's bring your landscape to life
          </h2>
          <p className="text-[#4A5447] text-base md:text-lg font-light mt-6 leading-relaxed max-w-md">
            Tell us about your project and we'll get back to you with a free,
            no-obligation estimate.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-center gap-4 group" data-testid="contact-phone">
              <div className="w-12 h-12 rounded-sm bg-[#1E3F20] flex items-center justify-center">
                <Phone size={20} strokeWidth={1.5} className="text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#4A5447]">Call Us</div>
                <div className="text-[#1A2118] font-semibold group-hover:text-[#1E3F20] transition-colors">{BUSINESS.phone}</div>
              </div>
            </a>
            <a href={BUSINESS.websiteUrl} target="_blank" rel="noreferrer" className="flex items-center gap-4 group" data-testid="contact-website">
              <div className="w-12 h-12 rounded-sm bg-[#1E3F20] flex items-center justify-center">
                <Globe size={20} strokeWidth={1.5} className="text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#4A5447]">Website</div>
                <div className="text-[#1A2118] font-semibold">{BUSINESS.website}</div>
              </div>
            </a>
            <a href={BUSINESS.mapsUrl} target="_blank" rel="noreferrer" className="flex items-center gap-4 group" data-testid="contact-address">
              <div className="w-12 h-12 rounded-sm bg-[#1E3F20] flex items-center justify-center">
                <MapPin size={20} strokeWidth={1.5} className="text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#4A5447]">Visit Us</div>
                <div className="text-[#1A2118] font-semibold group-hover:text-[#1E3F20] transition-colors">{BUSINESS.address}</div>
              </div>
            </a>
          </div>
        </div>

        <div className="bg-[#F7F5F0] border border-[#D8D3C8] rounded-sm p-8 md:p-10">
          {done ? (
            <div data-testid="contact-success" className="h-full flex flex-col items-center justify-center text-center py-12">
              <CheckCircle2 size={56} strokeWidth={1.5} className="text-[#1E3F20]" />
              <h3 className="font-serif-display text-3xl mt-5 text-[#1A2118]">Thank you!</h3>
              <p className="text-[#4A5447] font-light mt-2">Your request was received. We'll reach out shortly.</p>
              <button
                data-testid="contact-reset-btn"
                onClick={() => setDone(false)}
                className="mt-6 text-sm font-semibold text-[#1E3F20] border-b border-[#1E3F20] pb-1"
              >
                Send another request
              </button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="space-y-5">
              <div>
                <label className="text-sm font-medium text-[#1A2118]">Full Name *</label>
                <Input
                  data-testid="input-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="John Smith"
                  className="mt-2 bg-[#F7F5F0] border-[#D8D3C8] focus-visible:ring-[#1E3F20]"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-medium text-[#1A2118]">Phone *</label>
                  <Input
                    data-testid="input-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="(678) 826-6954"
                    className="mt-2 bg-[#F7F5F0] border-[#D8D3C8] focus-visible:ring-[#1E3F20]"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A2118]">Email</label>
                  <Input
                    data-testid="input-email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@email.com"
                    className="mt-2 bg-[#F7F5F0] border-[#D8D3C8] focus-visible:ring-[#1E3F20]"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1A2118]">Service Needed *</label>
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger data-testid="select-service" className="mt-2 bg-[#F7F5F0] border-[#D8D3C8] focus:ring-[#1E3F20]">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`service-option-${s.split(" ")[0].toLowerCase()}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1A2118]">Message</label>
                <Textarea
                  data-testid="input-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Tell us about your project..."
                  rows={4}
                  className="mt-2 bg-[#F7F5F0] border-[#D8D3C8] focus-visible:ring-[#1E3F20]"
                />
              </div>
              <button
                type="submit"
                data-testid="submit-quote-btn"
                disabled={loading}
                className="w-full rounded-sm bg-[#1E3F20] text-white py-4 text-sm font-semibold tracking-wide hover:bg-[#2B5A2D] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                {loading ? "Sending..." : "Request My Free Quote"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
